import type { WireHistoryPoint, WireService } from '@shared/contract';
import {
  STATUS_GLYPH,
  dependencies,
  duration,
  latency,
  terminalWorkers,
  uptime,
  worstCheck,
} from '@/lib/format';
import { SeverityChip, StatusChip } from './chips';
import { Sparkline } from './Sparkline';

const MAX_DEP_PILLS = 5;

interface Props {
  service: WireService;
  history: Record<number, WireHistoryPoint[]>;
  onOpen: (key: string) => void;
}

/**
 * The service card.
 *
 * Ordered by how fast each thing needs to be read: status edge and chip, then
 * name, then the four figures that decide whether to act, then the cause, then
 * history. It should answer "is this my problem right now?" without a click.
 */
export function ServiceCard({ service, history, onOpen }: Props) {
  const worst = worstCheck(service);
  const deps = dependencies(service);
  const workers = terminalWorkers(service);
  const unhealthyDeps = deps.filter((d) => d.status !== 'ok');
  const failing = service.checks.filter((c) => c.status !== 'ok').length;
  const passing = service.checks.length - failing;

  const allAcked = service.incidents.length > 0 && service.incidents.every((i) => i.acked);
  const stateLabel = service.status === 'ok' ? 'stable for' : 'down for';

  // A healthy service whose soft dependency is unhappy still deserves a line —
  // it is the early warning before the dependency takes the service with it.
  const cause =
    service.status !== 'ok' && worst?.lastError
      ? worst.lastError
      : unhealthyDeps.length && service.status === 'ok'
        ? unhealthyDeps.map((d) => `${d.name}: ${d.error ?? d.status}`).join('; ')
        : null;

  const open = () => onOpen(service.key);

  return (
    <article
      className={`card`}
      tabIndex={0}
      role="button"
      aria-label={`${service.name} — ${service.inMaintenance ? 'maintenance' : service.status}. Activate for details.`}
      onClick={open}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          open();
        }
      }}
    >
      <div className="card__body">
        <div className="card__top">
          <div className="card__ident">
            <div className="card__name">{service.name}</div>
            <div className="card__key">
              {service.key} · {service.env}
            </div>
          </div>
          <div className="card__chips">
            <StatusChip
              status={service.status}
              muted={service.inMaintenance}
              label={service.inMaintenance ? 'maintenance' : undefined}
            />
            {service.worstSeverity && (
              <SeverityChip severity={service.worstSeverity} acked={allAcked} />
            )}
          </div>
        </div>

        <div className="metrics">
          <Metric
            value={latency(worst?.lastLatencyMs)}
            label="Latency"
            tone={worst && worst.lastLatencyMs != null && worst.lastLatencyMs > 1000 ? 'warn' : undefined}
          />
          <Metric
            value={uptime(service.uptime24h)}
            label="24h uptime"
            tone={
              service.uptime24h == null
                ? 'none'
                : service.uptime24h < 99
                  ? 'bad'
                  : service.uptime24h < 99.9
                    ? 'warn'
                    : undefined
            }
          />
          <Metric
            value={`${passing}/${service.checks.length}`}
            label="Checks"
            tone={failing ? 'bad' : undefined}
          />
          <Metric value={worst ? duration(worst.since) : '—'} label={stateLabel} />
        </div>

        {workers.length > 0 && (
          <div className="workers" aria-label="Terminal workers">
            {workers.map((worker) => (
              <span
                key={worker.id}
                className={`worker is-${worker.status}`}
                title={worker.note ? `${worker.id}: ${worker.note}` : `${worker.id}: connected`}
              >
                <span className="worker__dot" aria-hidden="true">
                  {STATUS_GLYPH[worker.status]}
                </span>
                {worker.id}
                {worker.note && <span className="worker__note"> {worker.note}</span>}
              </span>
            ))}
          </div>
        )}

        {deps.length > 0 && (
          <div className="deps">
            {deps.slice(0, MAX_DEP_PILLS).map((dep) => (
              <span
                key={dep.name}
                className={`dep-pill is-${dep.status}`}
                title={dep.error ?? dep.status}
              >
                {dep.name}
                {dep.latencyMs != null ? ` ${dep.latencyMs}ms` : ''}
              </span>
            ))}
            {deps.length > MAX_DEP_PILLS && (
              <span className="dep-pill">+{deps.length - MAX_DEP_PILLS}</span>
            )}
          </div>
        )}

        {cause && (
          <div className={`card__cause${service.status === 'down' ? '' : ' is-degraded'}`}>
            {cause}
          </div>
        )}
      </div>

      {worst ? (
        <Sparkline history={history[worst.checkId] ?? []} checkKey={worst.key} />
      ) : (
        <div className="spark__empty">no checks configured</div>
      )}
    </article>
  );
}

function Metric({
  value,
  label,
  tone,
}: {
  value: string;
  label: string;
  tone?: 'bad' | 'warn' | 'none';
}) {
  return (
    <div className="metric">
      <div className={`metric__v${tone ? ` is-${tone}` : ''}`}>{value}</div>
      <div className="metric__l">{label}</div>
    </div>
  );
}
